/**
 * Teacher Calendar Page
 *
 * Calendar view showing all classes by day with quick actions
 * to access Meet/Whiteboard and manage schedule changes.
 */

import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useCreateScheduleChange,
  useDeleteScheduleChange,
  useScheduleChanges,
  useTeacherCalendar,
} from "../../hooks/useTeacher";
import type { CalendarClass, CreateScheduleChangeRequest } from "../../types/teacher";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Get calendar days for a month
function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const days: (number | null)[] = [];
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  return days;
}

// Format time for display
function formatTime(time: string): string {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

// Format date for display
function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function TeacherCalendarPage() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState<CalendarClass | null>(null);
  const [changeType, setChangeType] = useState<"cancelled" | "rescheduled">("cancelled");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const [confirmChange, setConfirmChange] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [changeToUndo, setChangeToUndo] = useState<string | null>(null);

  const { data, isLoading, error } = useTeacherCalendar({
    year: currentYear,
    month: currentMonth + 1, // API expects 1-indexed month
  });

  const { data: scheduleChanges, isLoading: isLoadingChanges } = useScheduleChanges({ isActive: true });
  const createChange = useCreateScheduleChange();
  const deleteChange = useDeleteScheduleChange();

  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  // Get classes for selected date
  const selectedDateClasses = useMemo(() => {
    if (!selectedDate || !data?.classesByDate) return [];
    return data.classesByDate[selectedDate] || [];
  }, [selectedDate, data?.classesByDate]);

  // Navigate months
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setSelectedDate(today.toISOString().split("T")[0]);
  };

  // Handle opening schedule change modal
  const openChangeModal = (cls: CalendarClass, _date: string) => {
    setSelectedClass(cls);
    setNewDate("");
    setNewTime(cls.time || "");
    setReason("");
    setChangeType("cancelled");
    setConfirmChange(false);
    setShowChangeModal(true);
  };

  // Handle creating schedule change
  const handleCreateChange = async () => {
    if (!selectedClass || !selectedDate) return;

    const originalTime = selectedClass.time || null;

    const requestData: CreateScheduleChangeRequest = {
      classGroup: selectedClass.id,
      changeType,
      originalDate: selectedDate,
      originalTime: originalTime || undefined,
      reason,
    };

    if (changeType === "rescheduled") {
      if (!newDate) {
        toast.error("Please select a new date");
        return;
      }
      requestData.newDate = newDate;
      requestData.newTime = newTime || undefined;
    }

    const toastId = toast.loading(
      changeType === "cancelled" ? "Cancelling class..." : "Rescheduling class..."
    );

    try {
      await createChange.mutateAsync(requestData);
      toast.success(
        changeType === "cancelled"
          ? "Class cancelled successfully"
          : "Class rescheduled successfully",
        { id: toastId }
      );
      setShowChangeModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to update schedule", {
        id: toastId,
      });
    }
  };

  // Handle opening undo confirmation modal
  const openUndoModal = (changeId: string) => {
    setChangeToUndo(changeId);
    setShowUndoModal(true);
  };

  // Handle deleting a schedule change
  const handleDeleteChange = async () => {
    if (!changeToUndo) return;

    const toastId = toast.loading("Reverting schedule change...");
    try {
      await deleteChange.mutateAsync(changeToUndo);
      toast.success("Schedule change reverted", { id: toastId });
      setShowUndoModal(false);
      setChangeToUndo(null);
    } catch (err: any) {
      toast.error("Failed to revert change", { id: toastId });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-600 font-medium">Error loading calendar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/teacher"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-3"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </Link>
          <h1 className="text-2xl font-bold text-earth-800">My Calendar</h1>
          <p className="text-earth-700 mt-1">
            View your classes and manage schedule changes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
              {/* Calendar Header */}
              <div className="px-4 sm:px-6 py-4 border-b border-earth-100 flex items-center justify-between">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-earth-100 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-earth-600"></i>
                </button>
                <div className="text-center">
                  <h2 className="font-semibold text-earth-800">
                    {MONTH_NAMES[currentMonth]} {currentYear}
                  </h2>
                  <button
                    onClick={goToToday}
                    className="text-xs text-olive-600 hover:text-olive-700"
                  >
                    Go to today
                  </button>
                </div>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-earth-100 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-earth-600"></i>
                </button>
              </div>

              {/* Weekday Headers */}
              <div className="grid grid-cols-7 border-b border-earth-100">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-medium text-earth-700 py-3"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={index} className="aspect-square bg-earth-50/50" />;
                  }

                  const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isToday =
                    day === today.getDate() &&
                    currentMonth === today.getMonth() &&
                    currentYear === today.getFullYear();
                  const isSelected = selectedDate === dateStr;
                  const dayClasses = data.classesByDate[dateStr] || [];
                  const hasClasses = dayClasses.length > 0;

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDate(dateStr)}
                      className={`aspect-square p-1 border-r border-b border-earth-100 transition-colors relative ${
                        isSelected
                          ? "bg-olive-100"
                          : isToday
                            ? "bg-amber-50"
                            : hasClasses
                              ? "bg-white hover:bg-earth-50"
                              : "bg-earth-50/30 hover:bg-earth-50"
                      }`}
                    >
                      <span
                        className={`text-sm ${
                          isToday
                            ? "font-bold text-amber-700"
                            : isSelected
                              ? "font-semibold text-olive-800"
                              : hasClasses
                                ? "text-earth-800"
                                : "text-earth-400"
                        }`}
                      >
                        {day}
                      </span>
                      {hasClasses && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                          {dayClasses.slice(0, 3).map((_, i) => (
                            <div
                              key={i}
                              className={`w-1.5 h-1.5 rounded-full ${
                                isSelected ? "bg-olive-600" : "bg-olive-400"
                              }`}
                            />
                          ))}
                          {dayClasses.length > 3 && (
                            <span className="text-[8px] text-earth-700">+{dayClasses.length - 3}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Selected Date Classes */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden sticky top-6">
              <div className="px-6 py-5 border-b border-earth-100">
                <h3 className="font-semibold text-earth-800">
                  {selectedDate ? formatDateDisplay(selectedDate) : "Select a date"}
                </h3>
                {selectedDate && (
                  <p className="text-sm text-earth-700">
                    {selectedDateClasses.length} class{selectedDateClasses.length !== 1 ? "es" : ""}
                  </p>
                )}
              </div>

              {!selectedDate ? (
                <div className="p-8 text-center text-earth-400">
                  <i className="ri-calendar-line text-4xl mb-3"></i>
                  <p className="text-sm">Click on a date to see classes</p>
                </div>
              ) : selectedDateClasses.length === 0 ? (
                <div className="p-8 text-center text-earth-400">
                  <i className="ri-calendar-close-line text-4xl mb-3"></i>
                  <p className="text-sm">No classes scheduled</p>
                </div>
              ) : (
                <div className="divide-y divide-earth-100 max-h-[600px] overflow-y-auto">
                  {selectedDateClasses.map((cls) => (
                    <div
                      key={`${cls.id}-${selectedDate}`}
                      className={`p-5 ${cls.isRescheduled ? "bg-amber-50" : ""}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-earth-800">
                              {cls.name}
                            </span>
                            <span className="px-1.5 py-0.5 bg-earth-100 text-earth-600 rounded text-xs">
                              {cls.levelCode}
                            </span>
                          </div>
                          <p className="text-sm text-earth-700 mt-1">
                            {formatTime(cls.time)} • {cls.classType}
                          </p>
                          {cls.isRescheduled && cls.rescheduleInfo && (
                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                              <i className="ri-information-line"></i>
                              <span>Rescheduled from {formatDateDisplay(cls.rescheduleInfo.originalDate || "")}</span>
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-earth-700 font-medium px-2.5 py-1 bg-earth-50 rounded-full">
                          {cls.studentCount} {cls.studentCount !== 1 ? "students" : "student"}
                        </span>
                      </div>

                      {/* Students */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {cls.students.map((student) => (
                            <span
                              key={student.id}
                              className="px-2 py-0.5 bg-earth-50 text-earth-600 rounded text-xs"
                            >
                              {student.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex flex-wrap gap-2.5">
                        {cls.meetLink && (
                          <a
                            href={cls.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 bg-olive-100 hover:bg-olive-200 text-olive-700 rounded-lg text-sm  transition-colors"
                          >
                            <i className="ri-video-chat-line text-base"></i>
                            <span>Meet</span>
                          </a>
                        )}
                        {cls.whiteboardUrl && (
                          <a
                            href={cls.whiteboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm  transition-colors"
                          >
                            <i className="ri-artboard-line text-base"></i>
                            <span>Whiteboard</span>
                          </a>
                        )}
                        <button
                          onClick={() => openChangeModal(cls, selectedDate)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg text-sm  transition-colors"
                        >
                          <i className="ri-calendar-event-line text-base"></i>
                          <span>Change</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Schedule Changes */}
        {scheduleChanges && scheduleChanges.length > 0 ? (
          <div className="mt-6 bg-white rounded-2xl border border-earth-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-earth-100">
              <h3 className="font-semibold text-earth-800">Schedule Changes</h3>
              <p className="text-sm text-earth-700 mt-1">
                Click "Undo" to revert any change and restore the original schedule. Students will be notified automatically.
              </p>
            </div>
            <div className="divide-y divide-earth-100">
              {scheduleChanges.map((change) => (
                <div
                  key={change.id}
                  className={`p-5 flex items-center justify-between ${
                    change.changeType === "cancelled" ? "bg-red-50" : "bg-amber-50"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          change.changeType === "cancelled"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {change.changeTypeDisplay}
                      </span>
                      <span className="font-medium text-earth-800">
                        {change.classGroupName}
                      </span>
                      <span className="px-1.5 py-0.5 bg-earth-100 text-earth-600 rounded text-xs">
                        {change.levelCode}
                      </span>
                    </div>
                    <p className="text-sm text-earth-600">
                      {change.changeType === "cancelled" ? (
                        <>Cancelled on {formatDateDisplay(change.originalDate)}</>
                      ) : (
                        <>
                          Moved from {formatDateDisplay(change.originalDate)} to{" "}
                          {change.newDate ? formatDateDisplay(change.newDate) : "TBD"}
                          {change.newTime && ` at ${formatTime(change.newTime)}`}
                        </>
                      )}
                    </p>
                    {change.reason && (
                      <p className="text-xs text-earth-700 mt-1">
                        Reason: {change.reason}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => openUndoModal(change.id)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors ml-4"
                    title="Undo this change and restore original schedule"
                  >
                    <i className="ri-arrow-go-back-line"></i>
                    <span>Undo</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : isLoadingChanges ? (
          <div className="mt-6 bg-white rounded-2xl border border-earth-200 overflow-hidden p-8 text-center">
            <Spinner size="md" />
          </div>
        ) : null}
      </div>

      {/* Undo Confirmation Modal */}
      {showUndoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-earth-100">
              <h3 className="text-lg font-bold text-earth-800">
                Undo Schedule Change?
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-earth-700 font-medium mb-2">
                    This will restore the original schedule
                  </p>
                  <p className="text-sm text-earth-600">
                    The class will be moved back to its original date and time.
                    Students will be notified about this change.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-earth-100 flex gap-3">
              <button
                onClick={() => {
                  setShowUndoModal(false);
                  setChangeToUndo(null);
                }}
                className="flex-1 px-4 py-2.5 bg-earth-100 text-earth-700 rounded-lg font-medium hover:bg-earth-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteChange}
                disabled={deleteChange.isPending}
                className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {deleteChange.isPending ? "Reverting..." : "Yes, Undo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Change Modal */}
      {showChangeModal && selectedClass && selectedDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-earth-100">
              <h3 className="text-lg font-bold text-earth-800">
                Schedule Change
              </h3>
              <p className="text-sm text-earth-700">
                {selectedClass.name} • {formatDateDisplay(selectedDate)}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Change Type */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">
                  Type of change
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setChangeType("cancelled")}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      changeType === "cancelled"
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-earth-200 hover:border-earth-300"
                    }`}
                  >
                    <i className="ri-close-circle-line text-lg mb-1"></i>
                    <p className="font-medium">Cancel</p>
                    <p className="text-xs opacity-70">Class won't happen</p>
                  </button>
                  <button
                    onClick={() => setChangeType("rescheduled")}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      changeType === "rescheduled"
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : "border-earth-200 hover:border-earth-300"
                    }`}
                  >
                    <i className="ri-calendar-event-line text-lg mb-1"></i>
                    <p className="font-medium">Reschedule</p>
                    <p className="text-xs opacity-70">Move to new date</p>
                  </button>
                </div>
              </div>

              {/* New Date/Time (for reschedule) */}
              {changeType === "rescheduled" && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-3">
                    Reschedule to
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="ri-calendar-line text-earth-400"></i>
                        </div>
                        <input
                          type="date"
                          value={newDate}
                          onChange={(e) => setNewDate(e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-earth-200 rounded-lg focus:outline-none focus:border-olive-500 focus:ring-2 focus:ring-olive-100 transition-all text-earth-800 text-sm hover:border-earth-300 cursor-pointer"
                          style={{
                            colorScheme: 'light',
                          }}
                        />
                      </div>
                      <p className="text-xs text-earth-700 mt-1.5">Date</p>
                    </div>
                    <div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="ri-time-line text-earth-400"></i>
                        </div>
                        <input
                          type="time"
                          value={newTime}
                          onChange={(e) => setNewTime(e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-earth-200 rounded-lg focus:outline-none focus:border-olive-500 focus:ring-2 focus:ring-olive-100 transition-all text-earth-800 text-sm hover:border-earth-300 cursor-pointer"
                          style={{
                            colorScheme: 'light',
                          }}
                        />
                      </div>
                      <p className="text-xs text-earth-700 mt-1.5">Time</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this class being changed?"
                  rows={2}
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-olive-500 resize-none"
                />
              </div>

              {/* Info */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                <i className="ri-information-line text-blue-600 mt-0.5"></i>
                <p className="text-sm text-blue-700">
                  Students in this class will receive a notification about this change.
                </p>
              </div>

              {/* Confirmation Checkbox */}
              <div className="flex items-start gap-2 p-3">
                <input
                  type="checkbox"
                  id="confirmChange"
                  checked={confirmChange}
                  onChange={(e) => setConfirmChange(e.target.checked)}
                  className="mt-0.5 w-5 h-5 text-amber-600"
                />
                <label htmlFor="confirmChange" className="text-sm text-amber-900 cursor-pointer">
                  <span className="">
                    I understand that this {changeType === "cancelled" ? "cancellation" : "reschedule"} will notify all students and cannot be undone easily.
                  </span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-earth-100 flex gap-3">
              <button
                onClick={() => setShowChangeModal(false)}
                className="flex-1 px-4 py-2.5 bg-earth-100 text-earth-700 rounded-lg font-medium hover:bg-earth-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChange}
                disabled={createChange.isPending || !confirmChange}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  changeType === "cancelled"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-amber-600 hover:bg-amber-700 text-white"
                } disabled:opacity-50`}
              >
                {createChange.isPending
                  ? "Saving..."
                  : changeType === "cancelled"
                    ? "Cancel Class"
                    : "Reschedule Class"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherCalendarPage;
